'use strict';
import geodist from 'geodist';
import find from 'lodash/find';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import isNumber from 'lodash/isNumber';
import {convertDMS, getLatLong} from '../../core-utilities/geo/utils';
import {locationComparator} from './comparators';


export const geoOptions = {
  unit: 'feet',
};

export const geoThreshold = 250; // feet

/**
 * @param {Array<ActiveTimer#GPSEntry>} a
 * @param {Array<ActiveTimer#GPSEntry>} b
 * @return {Array<ActiveTimer#GPSEntry>}
 */
export function mergeLoc(a, b) {
  const aEmpty = !a || a.length === 0;
  const bEmpty = !b || b.length === 0;

  if (aEmpty !== bEmpty) {
    // return a clone of the not-empty one
    return [...(aEmpty ? b : a)];
  } else if (aEmpty && bEmpty) {
    return [];
  } else {
    return cleanupLocations(a.concat(b));
  }
}

export function cleanupLocations(loc) {
  const result = [];
  const sorted = [
    ...loc,
  ];
  sorted.sort(locationComparator);
  result.push(sorted[0]);

  let prev = sorted[0];
  for (let i = 1, n = sorted.length; i < n; i++) {
    const next = sorted[i];
    if (prev && next && prev.tim === next.tim)
      continue;
    if (!isGPSProximate(prev, next))
      result.push(next);

    prev = next;
  }

  return result;
}

/**
 * @param {Array<ActiveTimer#GPSEntry>} oldLoc
 * @param {ActiveTimer#GPSEntry} item
 */
export function getLoc(oldLoc, item) {
  if (!item) {
    return oldLoc;
  } else if (oldLoc.length === 0) {
    return [item];
  } else {
    return cleanupLocations([
      ...oldLoc,
      item,
    ]);
  }
}

/**
 * @param {ActiveTimer#GPSEntry} a
 * @param {ActiveTimer#GPSEntry} b
 * @return {Boolean}
 */
export function isGPSProximate(a, b) {
  const aLoc = hasLoc(a);
  const bLoc = hasLoc(b);

  if (aLoc !== bLoc) {
    return false;
  } else if (!aLoc && !bLoc) {
    return true;
  } else {
    // compare proximity
    return geodist(a, b, geoOptions) < 50;
  }
}

/**
 * @param {ActiveTimer#GPSEntry} item
 * @return {Boolean}
 */
export function hasLoc(item) {
  return item && isNumber(item.lat) && isNumber(item.lon);
}

export function isLocCaptured(loc, existingLocCaptured) {
  if (existingLocCaptured) {
    return true;
  } else if (loc) {
    return !!find(loc, hasLoc);
  } else {
    return false;
  }
}

export function getLastLocation(loc) {
  let i = loc.length;
  while (i--) {
    if (isNumber(loc[i].lat))
      return loc[i];
  }
}

/**
 * Get lat, lon pair from geo data
 * @param {?Array|GeoJSON} geo
 * @return {LatLong}
 */
export function getCoords(geo) {
  if (isArray(geo)) {
    return {
      lat: geo[1],
      lon: geo[0],
    };
  } else if (isObject(geo)) {
    const latLong = getLatLong(geo);
    if (latLong !== null) {
      return latLong;
    }
  }
}

export function extractLocation(loc, start, end, offset = 30000) {
  if (!loc || loc.length === 0) {
    return [];
  } else {
    let startLocIdx = null;
    let endLocIdx = null;
    // TODO(perf): could probably save the last currLocIdx for each iteration to save looping through as much
    // Must be careful because of the way the loc data is compressed
    const locSize = loc.length;
    for (let currLocIdx = 0; currLocIdx < locSize; currLocIdx++) {
      const currLoc = loc[currLocIdx];
      const time = currLoc.tim;

      if (startLocIdx === null && isNumber(time) && time >= start) {
        if (time <= start + offset) {
          startLocIdx = currLocIdx;
        } else {
          startLocIdx = Math.max(0, currLocIdx - 1);
        }

        if (!isNumber(end)) {
          endLocIdx = locSize;
          break;
        }
      }

      if (isNumber(time) && isNumber(end) && time > end) {
        endLocIdx = currLocIdx;
        break;
      }
    }

    // Handle case where start time is after the last recorded gps result
    if (startLocIdx === null && locSize > 0) {
      const startLoc = loc[locSize - 1];
      const startTime = startLoc.tim;
      if (startTime < start) {
        startLocIdx = locSize - 1;
      }
    }

    if (startLocIdx !== null) {
      if (endLocIdx === null) endLocIdx = locSize;

      // test whether start is in the interval
      const startLoc = loc[startLocIdx];
      const startTime = startLoc.tim;
      if (startTime < start) {
        const output = [{
          ...startLoc,
          tim: start,
        }];

        return startLocIdx === endLocIdx ? output : output.concat(loc.slice(startLocIdx + 1, endLocIdx));
      } else {
        return loc.slice(startLocIdx, endLocIdx);
      }
    } else {
      return [];
    }
  }
}

export function renderLocationText(loc, geo) {
  let delta;
  let location;
  let locationText = '';
  const lastLocation = getLastLocation(loc);
  const expectedLocation = getCoords(geo);
  if (lastLocation && lastLocation.lat && lastLocation.lon
    && expectedLocation && expectedLocation.lat && expectedLocation.lon) {
    delta = getLocationDelta(lastLocation.lat, lastLocation.lon, expectedLocation.lat, expectedLocation.lon);
  } else if (lastLocation && lastLocation.val) {
    locationText = lastLocation.val;
  } else if (lastLocation && isNumber(lastLocation.lat) && isNumber(lastLocation.lon)) {
    locationText = convertDMS(lastLocation.lat, lastLocation.lon);
  }

  // TODO: translate
  if (isNumber(delta)) {
    if (delta === 0) {
      locationText = 'At location';
    } else if (delta < 1000) {
      locationText = `${delta.toFixed(0)} ft from location`;
    } else if (delta < 52800) {
      locationText = `${(delta / 5280).toFixed(1)} mi from location`;
    } else {
      locationText = `${(delta / 5280).toFixed(0)} mi from location`;
    }
  }

  return locationText;
}

function getLocationDelta(lat1, lon1, lat2, lon2) {
  const delta = geodist(
    {lat: lat1, lon: lon1},
    {lat: lat2, lon: lon2},
    geoOptions
  );

  if (delta < geoThreshold) {
    return 0;
  } else {
    return delta;
  }
}



// WEBPACK FOOTER //
// ./src/js/timers/utils/locationUtils.js